// A customer's Cost and Usage Report (CUR) sources (SCR-012, API-035): where each export lives,
// its status, the last successful read and unmapped rows; a source's row opens its summary with
// Verify, Ingest now and Disable; and
// the form to add one in one of the customer's AWS accounts linked with the CUR capability.
// Self-contained (fetches and mutates on its own); it reports its rows up so the customer's cost
// basis form knows whether a verified source exists.
import { useEffect, useState } from "preact/hooks";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ConfirmDialog } from "@nexus/ui";
import { errorId, Field } from "@nexus/ui";
import { fieldErrorsFrom } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import {
  CUR_SOURCE_STATUS_TONES,
  curAccountOptions,
  curLocation,
  curSourceRows,
  suggestedCurSource,
  validateNewCurSource,
  type CurAccountOption,
  type CurSourceRowView,
  type NewCurSourceInput,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

const EMPTY_INPUT: NewCurSourceInput = { awsAccountLinkId: "", bucket: "", prefix: "", exportName: "", region: "" };

export function CurSourcesSection({
  client,
  orgId,
  name,
  canWrite,
  onRows,
}: {
  client: ApiClient;
  orgId: string;
  name: string;
  canWrite: boolean;
  onRows?: ((rows: readonly CurSourceRowView[]) => void) | undefined;
}) {
  const [rows, setRows] = useState<CurSourceRowView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [input, setInput] = useState<NewCurSourceInput>(EMPTY_INPUT);
  const [busy, setBusy] = useState<string | null>(null);
  // A refused Add stays beside the form (with its field-level details), not over the table.
  const [addError, setAddError] = useState<ApiError | null>(null);
  // null: the account links could not be read, so the link id is typed.
  const [accounts, setAccounts] = useState<CurAccountOption[] | null>([]);
  const [disabling, setDisabling] = useState<CurSourceRowView | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/orgs/{orgId}/cur-sources", { path: { orgId } })
      .then((page) => {
        const next = curSourceRows(page.items);
        setRows(next);
        onRows?.(next);
      })
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [orgId]);
  useEffect(() => {
    client
      .get("/v1/admin/orgs/{orgId}/aws-accounts", { path: { orgId }, query: { limit: 500 } })
      .then((page) => setAccounts(curAccountOptions(page.items)))
      .catch(() => setAccounts(null));
  }, [orgId]);

  const fieldErrors = validateNewCurSource(input);
  const canSubmit = Object.keys(fieldErrors).length === 0;
  const serverFieldErrors = addError ? fieldErrorsFrom(addError) : {};

  const addSource = (event: Event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy("add");
    setAddError(null);
    client
      .post("/v1/admin/orgs/{orgId}/cur-sources", { path: { orgId }, body: input })
      .then(() => {
        setInput(EMPTY_INPUT);
        load();
      })
      .catch((err: unknown) => setAddError(asApiError(err)))
      .finally(() => setBusy(null));
  };

  const act = (sourceId: string, action: "verify" | "ingest" | "disable") => {
    setBusy(sourceId);
    const request =
      action === "verify"
        ? client.post("/v1/admin/orgs/{orgId}/cur-sources/{sourceId}/verify", { path: { orgId, sourceId } })
        : action === "ingest"
          ? client.post("/v1/admin/orgs/{orgId}/cur-sources/{sourceId}/ingest", { path: { orgId, sourceId } })
          : client.post("/v1/admin/orgs/{orgId}/cur-sources/{sourceId}/disable", { path: { orgId, sourceId } });
    request
      .then(() => {
        setDisabling(null);
        load();
      })
      .catch((err: unknown) => {
        setDisabling(null);
        setError(asApiError(err));
      })
      .finally(() => setBusy(null));
  };

  const chooseAccount = (linkId: string) => {
    const option = accounts?.find((account) => account.linkId === linkId);
    // Fill in what the bootstrap stack made for that account, leaving anything already typed.
    if (!option) return setInput({ ...input, awsAccountLinkId: linkId });
    const suggested = suggestedCurSource(option);
    setInput({
      awsAccountLinkId: linkId,
      bucket: input.bucket || suggested.bucket,
      prefix: input.prefix || suggested.prefix,
      exportName: input.exportName || suggested.exportName,
      region: input.region || suggested.region,
    });
  };

  const accountLabel = (linkId: string) => accounts?.find((account) => account.linkId === linkId)?.awsAccountId ?? linkId;

  const selected = rows.find((row) => row.id === selectedId) ?? null;
  const statusCell = (row: CurSourceRowView) => (
    <>
      <Chip tone={CUR_SOURCE_STATUS_TONES[row.status]}>{row.statusLabel}</Chip>
      {row.lastErrorCode && <span class="cell-sub mono">{row.lastErrorCode}</span>}
    </>
  );
  const lastSuccess = (row: CurSourceRowView) => (row.lastSuccessAt ? new Date(row.lastSuccessAt).toLocaleString() : "Never");

  const columns: Column<CurSourceRowView>[] = [
    {
      key: "export",
      header: "Export",
      cell: (row) => (
        <span class="name-with-id">
          <span class="mono">{curLocation(row)}</span>
          <span class="cell-sub">
            {row.exportName} · AWS account {accountLabel(row.awsAccountLinkId)}
          </span>
        </span>
      ),
    },
    { key: "region", header: "Region", cell: (row) => row.region },
    {
      key: "status",
      header: "Status",
      help: "common.status",
      cell: statusCell,
    },
    {
      key: "lastSuccess",
      header: "Last successful read",
      help: "finance.lastRun",
      cell: lastSuccess,
    },
    { key: "unmapped", header: "Unmapped rows", numeric: true, help: "finance.unmappedRows", cell: (row) => row.unmappedRows.toLocaleString() },
  ];

  const errorFor = (field: keyof NewCurSourceInput) => serverFieldErrors[field];

  return (
    <Card title="CUR sources" help="finance.curSources">
      <p class="card-note">The Cost and Usage Report (CUR 2.0) exports Nexus reads for {name}'s actual AWS bill.</p>
      <DataTable
        caption="CUR 2.0 exports linked to this customer"
        hideCaption
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage="No CUR sources yet. Add one below once an AWS account with the CUR capability is linked."
        onRowClick={(row) => setSelectedId(row.id)}
        rowLabel={(row) => `CUR source ${curLocation(row)}`}
      />
      {selected && (
        <RowDetail
          title={`CUR source ${selected.exportName}`}
          open
          onClose={() => setSelectedId(null)}
          facts={[
            { label: "Location", value: <span class="mono">{curLocation(selected)}</span> },
            { label: "AWS account", value: accountLabel(selected.awsAccountLinkId) },
            { label: "Region", value: selected.region },
            { label: "Status", value: statusCell(selected) },
            { label: "Last successful read", value: lastSuccess(selected) },
            { label: "Unmapped rows", value: selected.unmappedRows.toLocaleString() },
          ]}
          actions={
            <>
              {canWrite && selected.canDisable && (
                <button type="button" class="button button-danger" disabled={busy === selected.id} onClick={() => (setSelectedId(null), setDisabling(selected))}>
                  Disable
                </button>
              )}
              <button type="button" class="button" disabled={busy === selected.id || !selected.canIngest} onClick={() => act(selected.id, "ingest")}>
                Ingest now
              </button>
              <button type="button" class="button button-primary" disabled={busy === selected.id || !selected.canVerify} onClick={() => act(selected.id, "verify")}>
                Verify
              </button>
            </>
          }
        >
          {error && <ErrorBanner error={error} />}
        </RowDetail>
      )}
      <form class="stack-sm cur-source-form" onSubmit={addSource}>
        <h3>Add a CUR source</h3>
        <div class="form-grid">
          <Field id="cur-link-id" label="AWS account" help="finance.curAccount" error={errorFor("awsAccountLinkId")}>
            {accounts === null ? (
              <input
                id="cur-link-id"
                class="input"
                placeholder="AWS account link id"
                value={input.awsAccountLinkId}
                aria-describedby={errorFor("awsAccountLinkId") ? errorId("cur-link-id") : undefined}
                onInput={(e) => setInput({ ...input, awsAccountLinkId: e.currentTarget.value })}
              />
            ) : (
              <select
                id="cur-link-id"
                class="input"
                value={input.awsAccountLinkId}
                aria-describedby={errorFor("awsAccountLinkId") ? errorId("cur-link-id") : undefined}
                onChange={(e) => chooseAccount(e.currentTarget.value)}
              >
                <option value="">{accounts.length === 0 ? "No AWS account with the CUR capability" : "Choose an account"}</option>
                {accounts.map((account) => (
                  <option key={account.linkId} value={account.linkId}>
                    {account.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field id="cur-bucket" label="Bucket" help="finance.curBucket" error={errorFor("bucket")}>
            <input
              id="cur-bucket"
              class="input input-mono"
              value={input.bucket}
              aria-describedby={errorFor("bucket") ? errorId("cur-bucket") : undefined}
              onInput={(e) => setInput({ ...input, bucket: e.currentTarget.value })}
            />
          </Field>
          <Field id="cur-prefix" label="Prefix" help="finance.curPrefix" error={errorFor("prefix")}>
            <input id="cur-prefix" class="input input-mono" value={input.prefix} onInput={(e) => setInput({ ...input, prefix: e.currentTarget.value })} />
          </Field>
          <Field id="cur-export" label="Export name" help="finance.curExportName" error={errorFor("exportName")}>
            <input id="cur-export" class="input input-mono" value={input.exportName} onInput={(e) => setInput({ ...input, exportName: e.currentTarget.value })} />
          </Field>
          <Field id="cur-region" label="Region" help="finance.curRegion" error={errorFor("region")}>
            <input id="cur-region" class="input input-mono" value={input.region} onInput={(e) => setInput({ ...input, region: e.currentTarget.value })} />
          </Field>
        </div>
        {addError && <ErrorBanner error={addError} />}
        <div class="form-actions">
          <button type="submit" class="button button-primary" disabled={!canSubmit || busy === "add"}>
            Add CUR source
          </button>
        </div>
      </form>
      <ConfirmDialog
        title="Disable this CUR source?"
        open={disabling !== null}
        message={
          <p>
            Nexus stops reading <span class="mono">{disabling ? curLocation(disabling) : ""}</span> for good: verify and ingest are refused from then on, and it no
            longer counts as a verified source for the Actual AWS bill basis. Its past reads and costs stay as history. This cannot be undone.
          </p>
        }
        confirmLabel="Disable"
        busy={disabling !== null && busy === disabling.id}
        onConfirm={() => disabling && act(disabling.id, "disable")}
        onClose={() => setDisabling(null)}
      />
    </Card>
  );
}
